/**
 * Material name → illustrated equivalent.
 *
 * Surfaces that already ask for a Material icon by name (empty states, onboarding marks) can be
 * upgraded to the illustrated family without editing every call site: they resolve the name through
 * this table and fall back to the Material icon when there is no equivalent.
 *
 * Two rules govern what belongs here.
 *
 * **Only large marks may use it.** The illustrated family lives from 24px up
 * (`docs/guidelines/frontend.md`, "Le due famiglie di icone"). A caller that renders at 16px must
 * not resolve through this table — it would get a smudge. This is why the mapping is a helper the
 * caller opts into, and not something wired inside `Icon.tsx`: a global swap would hit every
 * button, table cell and badge in the product.
 *
 * **Several Material names collapse onto one illustration.** `Search`, `ManageSearch` and
 * `TravelExplore` are three glyphs for one idea, and the family has one object for it. Where a
 * distinction carries meaning it is kept — `Groups` (a pool) and `People` (a pair) stay separate.
 */

/** Material icon name → the `Ot*` component that replaces it on large marks. */
const OT_ICON_FOR: Record<string, string> = {
	// navigation and people
	Dashboard: 'OtDashboard',
	Search: 'OtSearch',
	ManageSearch: 'OtSearch',
	TravelExplore: 'OtPublic',
	SearchOff: 'OtSearch',
	PersonSearch: 'OtPersonSearch',
	Person: 'OtPerson',
	PersonOutline: 'OtPerson',
	PersonPin: 'OtPerson',
	PersonAdd: 'OtPerson',
	People: 'OtPeople',
	PeopleOutline: 'OtPeople',
	Groups: 'OtGroups',
	GroupAdd: 'OtGroups',
	Group: 'OtGroups',
	GroupWork: 'OtGroupWork',
	Workspaces: 'OtGroupWork',

	// work and projects
	Work: 'OtWork',
	WorkOutline: 'OtWork',
	WorkHistory: 'OtWork',
	BusinessCenter: 'OtBusinessCenter',
	Business: 'OtBusiness',
	AccountBalance: 'OtBusiness',
	LocationOn: 'OtPublic',
	Factory: 'OtBusiness',

	// collections
	FolderSpecial: 'OtFolderSpecial',
	FolderOpen: 'OtFolderSpecial',
	Folder: 'OtFolderSpecial',
	Collections: 'OtFolderSpecial',
	BookmarkAdded: 'OtFolderSpecial',
	BookmarkAdd: 'OtFolderSpecial',
	Bookmark: 'OtFolderSpecial',
	Videocam: 'OtVideo',
	PlayCircle: 'OtVideo',
	Movie: 'OtVideo',
	PhotoLibrary: 'OtFolderSpecial',
	Inventory: 'OtInventory',
	Inventory2: 'OtInventory',
	AutoAwesome: 'OtAutoAwesome',
	Star: 'OtAutoAwesome',
	Favorite: 'OtFavorite',
	FavoriteBorder: 'OtFavorite',

	// intelligence
	Insights: 'OtInsights',
	TrendingUp: 'OtInsights',
	Timeline: 'OtInsights',
	FilterAlt: 'OtInsights',
	ShowChart: 'OtInsights',
	Analytics: 'OtInsights',
	TrackChanges: 'OtTrackChanges',
	Visibility: 'OtVisibility',

	// communication
	Forum: 'OtForum',
	Chat: 'OtChat',
	ChatBubble: 'OtChat',
	Email: 'OtEmail',
	Inbox: 'OtInbox',
	Outbox: 'OtSend',
	Send: 'OtSend',
	Share: 'OtShare',
	Link: 'OtShare',
	InsertLink: 'OtShare',
	Campaign: 'OtCampaign',
	Notifications: 'OtNotifications',
	NotificationsNone: 'OtNotifications',

	// documents and money
	Description: 'OtDescription',
	EditNote: 'OtDescription',
	Edit: 'OtDescription',
	Draw: 'OtDescription',
	Article: 'OtDescription',
	Assignment: 'OtAssignment',
	AssignmentTurnedIn: 'OtAssignmentTurnedIn',
	Receipt: 'OtReceipt',
	ReceiptLong: 'OtReceipt',
	RequestQuote: 'OtRequestQuote',
	CardMembership: 'OtCardMembership',

	// status and process
	Gavel: 'OtGavel',
	Flag: 'OtFlag',
	EmojiEvents: 'OtEmojiEvents',
	Schedule: 'OtSchedule',
	CalendarToday: 'OtSchedule',
	Block: 'OtBlock',
	Lock: 'OtLock',
	Public: 'OtPublic',
	UploadFile: 'OtUpload',
	Upload: 'OtUpload',
	CloudUpload: 'OtUpload',
	FileUpload: 'OtUpload',

	// state marks — the icon next to a number or a message, never inside a button
	Bolt: 'OtBolt',
	OfflineBolt: 'OtBolt',
	CheckCircle: 'OtCheckCircle',
	CheckCircleOutline: 'OtCheckCircle',
	TaskAlt: 'OtCheckCircle',
	Verified: 'OtCheckCircle',
	Warning: 'OtWarning',
	WarningAmber: 'OtWarning',
	ReportProblem: 'OtWarning',
	Upgrade: 'OtUpgrade',
	RocketLaunch: 'OtUpgrade',
	Sensors: 'OtSensors',
	QueryStats: 'OtInsights',
	Whatshot: 'OtBolt',
	LocalFireDepartment: 'OtBolt',
	HelpOutline: 'OtHelp',
	Help: 'OtHelp',
	Info: 'OtHelp',
	InfoOutlined: 'OtHelp',
	Podcasts: 'OtSensors',
	SmartToy: 'OtAutoAwesome',
	Stars: 'OtAutoAwesome',
	History: 'OtSchedule',
	EditCalendar: 'OtSchedule',

	// ambient
	WbSunny: 'OtSun',
	WbTwilight: 'OtSun',
	LightMode: 'OtSun',
	DarkMode: 'OtMoon',
	NightsStay: 'OtMoon',
	Settings: 'OtSettings',
	Tune: 'OtSettings',
};

/**
 * Resolves a Material icon name to its illustrated equivalent, or returns the name unchanged when
 * the family has none. Safe to call with an `Ot*` name already: it is returned as-is.
 *
 * Only call this where the mark renders at 24px or more.
 */
export const otIconFor = (icon: string): string => {
	if (icon.startsWith('Ot')) return icon;
	return OT_ICON_FOR[icon] ?? icon;
};

/** True when the family has an illustrated equivalent for this Material name. */
export const hasOtIcon = (icon: string): boolean =>
	icon.startsWith('Ot') || icon in OT_ICON_FOR;

export default otIconFor;
